import { z } from 'zod';
import { isoDate } from './common';

/**
 * Organisation analytics (PRD 34): counts and durations over one workspace's
 * rows for a range — never a sentence anyone said, and never money: what a
 * meeting costs is the operator's to know (`tools/cost-report.ts`), not a page
 * a customer reads. Administrators only; everybody else gets the 404 every
 * refusal gets.
 */
export const analyticsQuery = z.object({
  from: isoDate.optional(),
  to: isoDate.optional(),
});
export type AnalyticsQuery = z.infer<typeof analyticsQuery>;

export const analyticsDay = z.object({
  day: z.string(),
  meetings: z.number().int(),
  seconds: z.number().int(),
  opened: z.number().int(),
  closed: z.number().int(),
});

export const orgAnalytics = z.object({
  range: z.object({ from: isoDate, to: isoDate }),
  use: z.object({
    meetings: z.number().int(),
    seconds: z.number().int(),
    averageSeconds: z.number().int(),
    members: z.number().int(),
    activeMembers7: z.number().int(),
    activeMembers30: z.number().int(),
    guestsMet: z.number().int(),
    bots: z.object({
      sent: z.number().int(),
      joined: z.number().int(),
      waitedAtTheDoor: z.number().int(),
      failed: z.number().int(),
      /** Median seconds from the call's end to the note written, or null. */
      secondsToNote: z.number().int().nullable(),
    }),
    reach: z.object({
      events: z.number().int(),
      withLink: z.number().int(),
      recorded: z.number().int(),
      noLink: z.number().int(),
      skipped: z.number().int(),
    }),
  }),
  produced: z.object({
    commitments: z.object({
      opened: z.number().int(),
      byGrade: z.record(z.string(), z.number().int()),
      closed: z.number().int(),
      byEnd: z.record(z.string(), z.number().int()),
      byPerson: z.number().int(),
      byListening: z.number().int(),
      open: z.number().int(),
      openOverWeek: z.number().int(),
      ownerGuessed: z.number().int(),
      medianDaysToClose: z.number().nullable(),
    }),
    decisions: z.object({ made: z.number().int(), superseded: z.number().int() }),
    notesWritten: z.number().int(),
    wordsByLanguage: z.record(z.string(), z.number().int()),
  }),
  /** How much of everything — hours, calls, bytes. No prices. */
  volume: z.object({
    recordedHours: z.number(),
    /** Model calls by what they were for: notes written, commitments read, questions answered. */
    aiCalls: z.array(z.object({ kind: z.string(), calls: z.number().int() })),
    videoBytesKept: z.number(),
    mcpCalls: z.number().int(),
  }),
  days: z.array(analyticsDay),
  series: z.array(
    z.object({
      seriesKey: z.string(),
      title: z.string(),
      instances: z.number().int(),
      seconds: z.number().int(),
      open: z.number().int(),
      closedByListening: z.number().int(),
    }),
  ),
  /**
   * Whether this workspace keeps the per-person table (`peopleAnalytics`). Off
   * by default; `people` is empty while it is off.
   */
  peopleEnabled: z.boolean(),
  /**
   * Members only — never a guest — and counts only: no talk time, no completion
   * rate, nothing that ranks one named colleague against another.
   */
  people: z.array(
    z.object({
      email: z.string(),
      name: z.string().nullable(),
      meetings: z.number().int(),
      seconds: z.number().int(),
      owes: z.number().int(),
      done: z.number().int(),
    }),
  ),
  members: z.array(
    z.object({
      email: z.string(),
      name: z.string().nullable(),
      role: z.string(),
      lastActiveAt: isoDate.nullable(),
      inactive: z.boolean(),
    }),
  ),
});
export type OrgAnalytics = z.infer<typeof orgAnalytics>;
